// shared/maintenance.ts
//
// „Hamarosan" (karbantartás / felkészülés) mód — EGYETLEN közös igazságforrás
// arról, hogy a publikus oldal épp látható-e. Ugyanezt a függvényt használja a
// szerver (API-kapu + HTML meta), a publikus kliens és az admin szerkesztő, így
// nem csúszhat el egymástól a három réteg. Nincs framework- vagy DB-függősége.

/** A „Hamarosan" mód beállításai (a site_settings JSONB-ben tárolva). */
export type MaintenanceSettings = {
  /**
   * Főkapcsoló. `false` / hiányzik → az oldal normálisan működik.
   * Bekapcsolva a publikus tartalom helyett a „Hamarosan" oldal jelenik meg
   * — KIVÉVE a bejelentkezett adminnak, aki így tovább tudja javítani a
   * tartalmat élőben.
   */
  enabled?: boolean;
  /**
   * Opcionális időzítés (ISO időbélyeg). Ha meg van adva, a mód CSAK ettől az
   * időponttól lép életbe (addig az oldal normálisan látszik).
   */
  startAt?: string | null;
  /**
   * Opcionális időzítés (ISO időbélyeg). Ha meg van adva, a mód ekkor
   * automatikusan kikapcsol — az oldal magától élesre vált.
   */
  endAt?: string | null;
  /** A „Hamarosan" oldal címe. Üres → `DEFAULT_MAINTENANCE_TITLE`. */
  title?: string;
  /** A cím alatti rövid szöveg. Üres → `DEFAULT_MAINTENANCE_MESSAGE`. */
  message?: string;
  /** Megjelenjen-e a telefonszám / e-mail cím a „Hamarosan" oldalon. */
  showContact?: boolean;
};

export const DEFAULT_MAINTENANCE_TITLE = "Hamarosan";
export const DEFAULT_MAINTENANCE_MESSAGE =
  "Oldalunk jelenleg megújul. Kérjük, nézzen vissza hamarosan!";
/** Projekt-szintű mód alapszövege (egy projekt tartalma rejtve). */
export const DEFAULT_PROJECT_MAINTENANCE_MESSAGE =
  "Ezt a projektet éppen frissítjük. A részletek hamarosan elérhetők lesznek!";

/** ISO időbélyeg → ms, érvénytelen / hiányzó érték → null. */
function timestamp(v: string | null | undefined): number | null {
  if (!v) return null;
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : null;
}

/**
 * Aktív-e most a „Hamarosan" mód?
 *
 * A főkapcsoló dönt; az időzítés SZŰKÍTI, mikor érvényes:
 *  - `startAt` megadva és még nem jött el   → még nem aktív
 *  - `endAt` megadva és már elmúlt          → már nem aktív (magától élesre vált)
 *
 * Kikapcsolt főkapcsolónál az időzítés nem csinál semmit — így egy régi,
 * elfelejtett dátum sem kapcsolhatja le váratlanul az oldalt.
 */
export function isMaintenanceActive(
  m: MaintenanceSettings | null | undefined,
  now: Date | number = Date.now(),
): boolean {
  if (!m?.enabled) return false;
  const t = typeof now === "number" ? now : now.getTime();
  const start = timestamp(m.startAt);
  if (start != null && t < start) return false;
  const end = timestamp(m.endAt);
  if (end != null && t >= end) return false;
  return true;
}

/**
 * Az admin státusz-sorához: mi a helyzet MOST, és — ha van időzítés — mikor
 * változik. A szöveg magyar, emberi; a `active` a tényleges állapot.
 */
export function maintenanceStatus(
  m: MaintenanceSettings | null | undefined,
  now: Date | number = Date.now(),
): { active: boolean; text: string } {
  const t = typeof now === "number" ? now : now.getTime();
  const active = isMaintenanceActive(m, t);
  if (!m?.enabled) {
    return { active: false, text: "Az oldal ÉLES — a látogatók a tartalmat látják." };
  }
  const start = timestamp(m.startAt);
  const end = timestamp(m.endAt);
  const fmt = (ms: number) =>
    new Date(ms).toLocaleString("hu-HU", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  if (start != null && t < start) {
    return {
      active: false,
      text: `Az oldal még ÉLES — a „Hamarosan" mód ${fmt(start)}-kor kapcsol be${
        end != null ? `, és ${fmt(end)}-kor vissza` : ""
      }.`,
    };
  }
  if (end != null && t >= end) {
    return {
      active: false,
      text: `Az oldal ÉLES — az időzített „Hamarosan" időszak ${fmt(end)}-kor lejárt.`,
    };
  }
  return {
    active: true,
    text: `„HAMAROSAN" MÓD AKTÍV — a látogatók nem látják a tartalmat${
      end != null ? `. Automatikusan élesre vált: ${fmt(end)}` : ""
    }.`,
  };
}
